import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  createConversation,
  createConversationFeedback,
  formatApiError,
  getAgent,
  getConversationFeedback,
  getFeedbackQueue,
  getKnowledgeSources,
  routeFeedbackQueueItem,
  sendMessage,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from '../components/ui/sonner';
import {
  ArrowLeft,
  Bot,
  Brain,
  BookOpen,
  CheckCircle2,
  Copy,
  ExternalLink,
  FlaskConical,
  GitBranch,
  Loader2,
  MessageSquare,
  Send,
  Sparkles,
  Wand2,
} from 'lucide-react';

const categoryLabels = {
  customer_service: 'Customer Service',
  appointment_setting: 'Appointment Setting',
  sales: 'Sales',
  routing: 'Routing Agent',
};

const feedbackPlaybooks = {
  prompt: {
    label: 'Prompt / behavior',
    issueType: 'prompt/behavior',
    destination: 'prompt',
    title: 'Prompt refinement path',
    description: 'Use this when the agent sounded off-brand, missed instructions, or handled tone poorly.',
    nextStep: 'Create a prompt-improvement record for this agent, then refine prompt/settings in Agent Studio.',
  },
  knowledge: {
    label: 'Knowledge gap',
    issueType: 'knowledge_gap',
    destination: 'knowledge',
    title: 'Knowledge update path',
    description: 'Use this when the answer should exist, but the agent lacked the right facts or documentation.',
    nextStep: 'Turn the correction into a KB draft, review it, and publish when approved.',
  },
  handoff: {
    label: 'Handoff / routing',
    issueType: 'routing/handoff',
    destination: 'routing',
    title: 'Routing refinement path',
    description: 'Use this when the wrong agent handled the request or a transfer should have happened sooner.',
    nextStep: 'Create a routing-improvement record and adjust handoff rules in Agent Studio.',
  },
  issue: {
    label: 'Bug / product issue',
    issueType: 'escalation/other',
    destination: 'prompt',
    title: 'Operational follow-up path',
    description: 'Use this when behavior feels broken, errors appear, or the workflow needs engineering support.',
    nextStep: 'Capture the issue clearly; if it is still agent-behavior related, route it into a prompt improvement for operator review.',
  },
};

const routeOptions = [
  { value: 'prompt', label: 'Prompt improvement', icon: Brain },
  { value: 'knowledge', label: 'Knowledge draft', icon: BookOpen },
  { value: 'routing', label: 'Routing improvement', icon: GitBranch },
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const chunkTypingDelay = (text) => {
  const len = (text || '').trim().length;
  return Math.max(450, Math.min(1200, 300 + len * 8));
};

const ProvingGroundsPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const messagesContainerRef = useRef(null);

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);

  const [feedbackType, setFeedbackType] = useState('prompt');
  const [feedbackScenario, setFeedbackScenario] = useState('');
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [feedbackCorrection, setFeedbackCorrection] = useState('');
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [queueItems, setQueueItems] = useState([]);
  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [routingState, setRoutingState] = useState({});
  const [routingBusyId, setRoutingBusyId] = useState(null);

  useEffect(() => {
    const loadAgent = async () => {
      setLoading(true);
      try {
        const [{ data }, queueRes, sourcesRes] = await Promise.all([
          getAgent(agentId),
          getFeedbackQueue(),
          getKnowledgeSources(),
        ]);
        setAgent(data);
        setQueueItems((queueRes.data || []).filter((item) => item.agent_id === agentId));
        setKnowledgeSources(sourcesRes.data || []);
      } catch (error) {
        console.error('Failed to load agent:', error);
        toast.error('Unable to load this agent in Proving Grounds.');
        navigate('/agents');
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, [agentId, navigate]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [chatMessages, chatLoading]);

  const feedbackGuide = useMemo(() => feedbackPlaybooks[feedbackType] || feedbackPlaybooks.prompt, [feedbackType]);

  const refreshConversationFeedback = async (convId) => {
    if (!convId) return;
    const [, queueRes] = await Promise.all([
      getConversationFeedback(convId),
      getFeedbackQueue(),
    ]);
    setQueueItems((queueRes.data || []).filter((item) => item.agent_id === agentId));
  };

  const handleCopyConversationId = async () => {
    if (!conversationId) return;
    try {
      await navigator.clipboard.writeText(conversationId);
      toast.success('Chat ID copied');
    } catch (error) {
      toast.error('Failed to copy chat ID');
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || !agentId) return;

    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      let convId = conversationId;
      if (!convId) {
        const conversation = await createConversation({ agent_id: agentId, channel: 'web' });
        convId = conversation.data.id;
        setConversationId(convId);
      }

      const response = await sendMessage(convId, userMessage);

      const assistant = response.data?.assistant_message;
      const assistantChunks = Array.isArray(assistant?.chunks) && assistant.chunks.length > 0
        ? assistant.chunks
        : (assistant?.content ? [assistant.content] : []);

      if (assistantChunks.length > 0) {
        for (let i = 0; i < assistantChunks.length; i += 1) {
          const chunk = assistantChunks[i];
          if (i > 0) {
            setChatLoading(true);
            await wait(chunkTypingDelay(chunk));
            setChatLoading(false);
          }
          setChatMessages((prev) => [
            ...prev,
            {
              id: i === assistantChunks.length - 1 ? assistant.id : `${assistant.id}-chunk-${i}`,
              role: 'assistant',
              content: chunk,
            },
          ]);
        }
      }

      if (response.data?.scheduling?.slots?.length) {
        const slotSummary = response.data.scheduling.slots
          .map((slot) => `• ${slot.display}`)
          .join('\n');
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'system',
            content: `Availability preview (${response.data.scheduling.timezone}):\n${slotSummary}`,
          },
        ]);
      }

      if (response.data?.handoff?.announce && response.data?.handoff?.announcement) {
        setChatMessages((prev) => [
          ...prev,
          {
            role: 'system',
            content: response.data.handoff.announcement,
          },
        ]);
      }

      await refreshConversationFeedback(convId);
    } catch (error) {
      console.error('Failed to send message:', error);
      setChatMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, something went wrong while testing this agent.' },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const latestAssistantMessage = [...chatMessages].reverse().find((message) => message.role === 'assistant' && message.id);

  const handleSaveFeedback = async () => {
    if (!conversationId) {
      toast.error('Run a test conversation first so feedback has real context.');
      return;
    }
    if (!feedbackScenario.trim() && !feedbackNotes.trim() && !feedbackCorrection.trim()) {
      toast.error('Add scenario details, notes, or a corrected answer before saving.');
      return;
    }

    setSavingFeedback(true);
    try {
      await createConversationFeedback(conversationId, {
        message_id: latestAssistantMessage?.id || null,
        feedback_type: feedbackCorrection.trim() ? 'corrected' : 'bad',
        issue_type: feedbackGuide.issueType,
        correction_text: feedbackCorrection.trim() || null,
        notes: [feedbackScenario.trim(), feedbackNotes.trim()].filter(Boolean).join('\n\n') || null,
      });
      setFeedbackScenario('');
      setFeedbackNotes('');
      setFeedbackCorrection('');
      await refreshConversationFeedback(conversationId);
      toast.success('Feedback captured for operator review.', { description: feedbackGuide.nextStep });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSavingFeedback(false);
    }
  };

  const getRouteDraft = (item) => {
    const suggestedDestination = item.route_destination || (item.issue_type === 'knowledge_gap' ? 'knowledge' : item.issue_type === 'routing/handoff' ? 'routing' : 'prompt');
    return routingState[item.id] || {
      destination: suggestedDestination,
      title: item.message_content || `Improvement from ${feedbackPlaybooks[feedbackType]?.label || 'feedback'}`,
      summary: item.notes || item.correction_text || '',
      suggestion: item.correction_text || '',
      route_notes: '',
      source_id: '',
      publish: false,
    };
  };

  const updateRouteDraft = (feedbackId, patch) => {
    const baseItem = queueItems.find((item) => item.id === feedbackId);
    setRoutingState((prev) => ({
      ...prev,
      [feedbackId]: {
        ...(baseItem ? getRouteDraft(baseItem) : {}),
        ...(prev[feedbackId] || {}),
        ...patch,
      },
    }));
  };

  const handleRouteFeedback = async (item) => {
    const draft = getRouteDraft(item);
    setRoutingBusyId(item.id);
    try {
      await routeFeedbackQueueItem(item.id, {
        ...draft,
        tags: draft.destination === 'knowledge' ? ['feedback', 'operator-reviewed'] : undefined,
      });
      if (conversationId) await refreshConversationFeedback(conversationId);
      else {
        const queueRes = await getFeedbackQueue();
        setQueueItems((queueRes.data || []).filter((entry) => entry.agent_id === agentId));
      }
      toast.success(
        draft.destination === 'knowledge'
          ? draft.publish
            ? 'Knowledge correction published.'
            : 'Knowledge draft created.'
          : draft.destination === 'routing'
            ? 'Routing improvement recorded.'
            : 'Prompt improvement recorded.'
      );
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setRoutingBusyId(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background" data-testid="proving-grounds-page">
        <div className="border-b border-border bg-card/95 backdrop-blur sticky top-0 z-20">
          <div className="px-6 py-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-3">
              <button
                onClick={() => navigate(`/agents/${agentId}`)}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
                data-testid="back-to-agent-studio"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/10">Proving Grounds</Badge>
                  <Badge variant="outline">{categoryLabels[agent?.category] || agent?.category || 'Agent'}</Badge>
                </div>
                <h1 className="text-2xl font-semibold tracking-tight font-['Outfit'] mt-2">{agent?.name}</h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                  Test live conversations, capture feedback, and route reviewed items into prompt, knowledge, or routing improvements without autonomous retraining.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" className="rounded-lg gap-2" asChild>
                <Link to={`/agents/${agentId}/widget`} data-testid="open-widget-preview-button">
                  <BookOpen className="w-4 h-4" />
                  Widget & Embed
                </Link>
              </Button>
              <Button className="rounded-lg gap-2" asChild>
                <Link to={`/agents/${agentId}/edit`} data-testid="open-agent-studio-button">
                  <Wand2 className="w-4 h-4" />
                  Refine in Agent Studio
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-[1.45fr_0.95fr] gap-6 items-start">
            <section className="apollo-card overflow-hidden">
              <div className="p-5 border-b border-border flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-semibold font-['Outfit'] flex items-center gap-2">
                    <FlaskConical className="w-4 h-4 text-primary" />
                    Live test session
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Try realistic prompts, edge cases, and handoff scenarios with feedback tied to the actual conversation.
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="text-xs text-muted-foreground">{conversationId ? 'Session active' : 'No session yet'}</div>
                  {conversationId ? (
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <div className="rounded-full border border-border bg-secondary/40 px-3 py-1 text-xs font-mono" data-testid="proving-grounds-conversation-id">
                        Chat ID: {conversationId}
                      </div>
                      <Button type="button" variant="outline" size="sm" className="rounded-lg gap-1.5" onClick={handleCopyConversationId}>
                        <Copy className="w-3.5 h-3.5" />
                        Copy ID
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="rounded-lg gap-1.5" onClick={() => navigate('/conversations')}>
                        <ExternalLink className="w-3.5 h-3.5" />
                        Open Logs
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="p-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-xl border border-border bg-secondary/30 p-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Tone</p>
                    <p className="font-medium mt-2 capitalize">{agent?.tone || 'Professional'}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-secondary/30 p-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Model</p>
                    <p className="font-medium mt-2">{agent?.llm_provider} / {agent?.llm_model}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-secondary/30 p-4">
                    <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Improvement loop</p>
                    <p className="font-medium mt-2">Test → review → route → apply</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-background min-h-[460px] flex flex-col">
                  <div className="p-4 border-b border-border bg-card">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Bot className="w-4 h-4 text-primary" />
                      Conversation sandbox
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Use this space to validate greetings, knowledge recall, objection handling, and handoffs.
                    </p>
                  </div>

                  <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/10">
                    {agent?.greeting_message && chatMessages.length === 0 && (
                      <div className="chat-message assistant" data-testid="proving-grounds-greeting">
                        {agent.greeting_message}
                      </div>
                    )}

                    {chatMessages.length === 0 && !agent?.greeting_message && (
                      <div className="h-full flex items-center justify-center text-center px-6">
                        <div>
                          <MessageSquare className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                          <p className="text-sm font-medium">Start testing this agent</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Try realistic customer questions, edge cases, or a scripted QA scenario.
                          </p>
                        </div>
                      </div>
                    )}

                    {chatMessages.map((message, index) => (
                      <div key={index} className={`chat-message ${message.role}`} data-testid={`proving-message-${index}`}>
                        {message.content}
                      </div>
                    ))}

                    {chatLoading && (
                      <div className="chat-message assistant">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    )}
                  </div>

                  <div className="p-4 border-t border-border bg-card">
                    <div className="flex gap-2">
                      <Input
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Type a scenario or test message..."
                        className="rounded-lg"
                        disabled={chatLoading}
                        data-testid="proving-grounds-input"
                      />
                      <Button
                        onClick={handleSendMessage}
                        disabled={chatLoading || !chatInput.trim()}
                        className="rounded-lg"
                        data-testid="proving-grounds-send"
                      >
                        {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="space-y-6">
              <div className="apollo-card p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold font-['Outfit']">Performance feedback</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Capture a reviewed note, then route it into an operator-approved improvement path.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Feedback type</Label>
                    <Select value={feedbackType} onValueChange={setFeedbackType}>
                      <SelectTrigger className="rounded-lg" data-testid="feedback-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(feedbackPlaybooks).map(([key, value]) => (
                          <SelectItem key={key} value={key}>{value.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="rounded-xl border border-border bg-secondary/30 p-4">
                    <p className="text-sm font-medium">{feedbackGuide.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{feedbackGuide.description}</p>
                    <p className="text-xs text-primary mt-3">Next step: {feedbackGuide.nextStep}</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Scenario being tested</Label>
                    <Input
                      value={feedbackScenario}
                      onChange={(e) => setFeedbackScenario(e.target.value)}
                      placeholder="e.g. Asked for a refund policy after a pricing objection"
                      className="rounded-lg"
                      data-testid="feedback-scenario-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Observed behavior and review notes</Label>
                    <Textarea
                      value={feedbackNotes}
                      onChange={(e) => setFeedbackNotes(e.target.value)}
                      rows={5}
                      className="rounded-lg resize-none"
                      placeholder="What happened, why it needs review, and what an operator should change."
                      data-testid="feedback-notes-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Corrected answer or desired behavior</Label>
                    <Textarea
                      value={feedbackCorrection}
                      onChange={(e) => setFeedbackCorrection(e.target.value)}
                      rows={4}
                      className="rounded-lg resize-none"
                      placeholder="If you already know the right answer or wording, put it here so it can become a KB draft or improvement suggestion."
                    />
                  </div>

                  <Button onClick={handleSaveFeedback} disabled={savingFeedback} className="w-full rounded-lg gap-2" data-testid="save-feedback-button">
                    {savingFeedback ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Save reviewed feedback
                  </Button>

                  <p className="text-[11px] text-muted-foreground">
                    Feedback is stored and routed only after operator review. This workflow creates review artifacts; it does not auto-retrain prompts or publish knowledge without approval.
                  </p>
                </div>
              </div>

              <div className="apollo-card p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold font-['Outfit']">Operator training queue</h3>
                    <p className="text-sm text-muted-foreground mt-1">Route this agent’s reviewed feedback into concrete improvement records.</p>
                  </div>
                  <Badge variant="outline">{queueItems.length}</Badge>
                </div>

                {queueItems.length === 0 ? (
                  <div className="border border-dashed border-border rounded-xl p-6 text-center">
                    <p className="text-sm font-medium">No feedback in queue yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Capture notes from a live test first, then route the best items into prompt, knowledge, or routing improvements.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {queueItems.map((item) => {
                      const routeDraft = getRouteDraft(item);
                      return (
                        <div key={item.id} className="rounded-xl border border-border p-4 bg-secondary/20 space-y-3">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-primary/10 text-primary hover:bg-primary/10">{item.issue_type || item.feedback_type}</Badge>
                              <Badge variant={item.status === 'reviewed' ? 'default' : 'outline'}>{item.status}</Badge>
                              {item.route_destination && <Badge variant="outline">routed to {item.route_destination}</Badge>}
                            </div>
                            <span className="text-[11px] text-muted-foreground">{new Date(item.created_at).toLocaleString()}</span>
                          </div>

                          {item.message_content && <p className="text-sm font-medium">Prompt: {item.message_content}</p>}
                          {item.notes && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{item.notes}</p>}
                          {item.correction_text && (
                            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 text-sm">
                              <p className="font-medium text-emerald-700 dark:text-emerald-400">Corrected answer</p>
                              <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{item.correction_text}</p>
                            </div>
                          )}

                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="space-y-2">
                              <Label>Route to</Label>
                              <Select value={routeDraft.destination} onValueChange={(value) => updateRouteDraft(item.id, { destination: value })}>
                                <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {routeOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {routeDraft.destination === 'knowledge' && (
                              <div className="space-y-2">
                                <Label>Knowledge source</Label>
                                <Select value={routeDraft.source_id || '__auto__'} onValueChange={(value) => updateRouteDraft(item.id, { source_id: value === '__auto__' ? '' : value })}>
                                  <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__auto__">Auto-create / Feedback Corrections</SelectItem>
                                    {knowledgeSources.map((source) => (
                                      <SelectItem key={source.id} value={source.id}>{source.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label>Improvement title</Label>
                            <Input value={routeDraft.title} onChange={(e) => updateRouteDraft(item.id, { title: e.target.value })} className="rounded-lg" />
                          </div>

                          <div className="space-y-2">
                            <Label>Operator summary</Label>
                            <Textarea rows={3} value={routeDraft.summary} onChange={(e) => updateRouteDraft(item.id, { summary: e.target.value })} className="rounded-lg resize-none" />
                          </div>

                          <div className="space-y-2">
                            <Label>{routeDraft.destination === 'knowledge' ? 'Draft answer / correction' : routeDraft.destination === 'routing' ? 'Suggested routing rule change' : 'Suggested prompt / behavior change'}</Label>
                            <Textarea rows={3} value={routeDraft.suggestion} onChange={(e) => updateRouteDraft(item.id, { suggestion: e.target.value })} className="rounded-lg resize-none" />
                          </div>

                          <div className="space-y-2">
                            <Label>Review note</Label>
                            <Input value={routeDraft.route_notes} onChange={(e) => updateRouteDraft(item.id, { route_notes: e.target.value })} className="rounded-lg" placeholder="Optional note about why this routing path was chosen" />
                          </div>

                          {routeDraft.destination === 'knowledge' && (
                            <label className="flex items-center gap-2 text-sm text-muted-foreground">
                              <input type="checkbox" checked={!!routeDraft.publish} onChange={(e) => updateRouteDraft(item.id, { publish: e.target.checked })} />
                              Publish immediately after creating the draft
                            </label>
                          )}

                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <p className="text-[11px] text-muted-foreground">
                              This creates a review artifact linked back to the feedback item. Operators still decide when to apply the actual prompt/routing changes.
                            </p>
                            <Button onClick={() => handleRouteFeedback(item)} disabled={routingBusyId === item.id} className="rounded-lg gap-2">
                              {routingBusyId === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : item.status === 'reviewed' ? <CheckCircle2 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                              {routeDraft.destination === 'knowledge'
                                ? routeDraft.publish ? 'Publish knowledge correction' : 'Create knowledge draft'
                                : routeDraft.destination === 'routing'
                                  ? 'Create routing record'
                                  : 'Create prompt record'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProvingGroundsPage;
